import axios from "axios";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Calendar2Week,
  People,
  Gear,
  BoxArrowRight,
  ChevronDown,
  List,
  X,
  CreditCard,
  Link45deg,
  Lock,
  Unlock,
} from "react-bootstrap-icons";

import { useAuthContext } from "../hooks/useAuthContext";
import SubscribeModal from "../modals/SubscribeModal";
import PrivacyPinModal from "../modals/PrivacyPinModal";
import "./AppLayout.css";
import { ProjectName } from "../constants/ProjectName";
import { ApiConnectionString } from "../constants/ApiConntection";

const logo = "/logo192.png";

export default function AppLayout({ profile }) {
  const navi = useNavigate();

  const { user, dispatch } = useAuthContext();

  const [menuOpen, setMenuOpen] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  const [privacyStatus, setPrivacyStatus] = useState(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);

  const [privacyRefreshKey, setPrivacyRefreshKey] = useState(0);

  const firstName =
    profile?.firstName ||
    profile?.FirstName ||
    "";

  const lastName =
    profile?.lastName ||
    profile?.LastName ||
    profile?.secondName ||
    "";

  const fullName =
    firstName || lastName
      ? `${firstName} ${lastName}`
      : "Loading...";

  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() ||
    "SP";

  const activeSubscription =
    profile?.activeSubscription ??
    profile?.ActiveSubscription ??
    false;

  const freeTrialExpiresAt =
    profile?.freeTrialExpiresAt ??
    profile?.FreeTrialExpiresAt ??
    null;

  const freeTrialExpired =
    freeTrialExpiresAt &&
    new Date(freeTrialExpiresAt) < new Date();

  const shouldShowSubscribeButton =
    freeTrialExpired &&
    activeSubscription === false;

  const privacyEnabled =
    privacyStatus?.enabled ??
    privacyStatus?.Enabled ??
    false;

  const privacyUnlocked =
    privacyStatus?.unlocked ??
    privacyStatus?.Unlocked ??
    false;

  const getPrivacyStatus = async () => {
    if (!user?.token) {
      return;
    }

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/privacy-pin/status`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setPrivacyStatus(response.data || {});
    } catch (error) {
      console.error(
        "Could not load privacy PIN status:",
        error
      );
    }
  };

  useEffect(() => {
    if (user?.token) {
      getPrivacyStatus();
    }
  }, [user?.token]);

  useEffect(() => {
    const handlePrivacyChanged = () => {
      getPrivacyStatus();
    };

    window.addEventListener(
      "privacy-pin-changed",
      handlePrivacyChanged
    );

    return () => {
      window.removeEventListener(
        "privacy-pin-changed",
        handlePrivacyChanged
      );
    };
  }, [user?.token]);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const openSubscribeModal = () => {
    setShowSubscribeModal(true);
    closeMenu();
  };

  const openPrivacyModal = () => {
    setShowPrivacyModal(true);
    closeMenu();
  };

  const closePrivacyModal = () => {
    setShowPrivacyModal(false);
  };

  const handlePrivacyUnlocked = async () => {
    setShowPrivacyModal(false);

    await getPrivacyStatus();

    setPrivacyRefreshKey(
      (current) => current + 1
    );
  };

  const lockPrivacy = async () => {
    if (!user?.token) {
      return;
    }

    try {
      setPrivacyLoading(true);

      await axios.post(
        `${ApiConnectionString}/api/privacy-pin/lock`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      await getPrivacyStatus();

      closeMenu();

      setPrivacyRefreshKey(
        (current) => current + 1
      );
    } catch (error) {
      console.error(
        "Could not lock client details:",
        error
      );
    } finally {
      setPrivacyLoading(false);
    }
  };

  const privacyButtonClicked = () => {
    if (privacyUnlocked) {
      lockPrivacy();
      return;
    }

    openPrivacyModal();
  };

  const logout = async () => {
    try {
      if (user?.token) {
        await axios.post(
          `${ApiConnectionString}/api/privacy-pin/lock`,
          {},
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
      }
    } catch (error) {
      console.error(
        "Could not lock privacy PIN:",
        error
      );
    }

    try {
      await axios.post(
        `${ApiConnectionString}/api/logout`,
        {},
        {
          withCredentials: true,
        }
      );
    } catch (error) {
      console.error(
        "Could not revoke refresh token:",
        error
      );
    }

    dispatch({
      type: "LOGOUT",
      payload: {},
    });

    closeMenu();

    navi("/");
  };

  return (
    <>
      <SubscribeModal
        show={showSubscribeModal}
        onClose={() =>
          setShowSubscribeModal(false)
        }
      />

      <PrivacyPinModal
        show={showPrivacyModal}
        token={user?.token}
        onClose={closePrivacyModal}
        onUnlocked={handlePrivacyUnlocked}
      />

      <div className="app-shell">
        <aside
          className={`app-sidebar ${
            menuOpen ? "open" : ""
          }`}
        >
          <button
            type="button"
            className="sidebar-mobile-close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <X />
          </button>

          <div className="sidebar-top">
            <div className="sidebar-brand">
              <div className="brand-mark">
                <img
                  src={logo}
                  alt={`${ProjectName} logo`}
                />
              </div>

              <div>
                <strong>
                  {ProjectName}
                </strong>

                <span>
                  Therapy Diary
                </span>
              </div>
            </div>

            {shouldShowSubscribeButton && (
              <button
                type="button"
                className="sidebar-subscribe-btn"
                onClick={openSubscribeModal}
              >
                <CreditCard />

                <span>
                  Subscribe
                </span>
              </button>
            )}

            {privacyEnabled && (
              <button
                type="button"
                className="sidebar-privacy-btn"
                onClick={privacyButtonClicked}
                disabled={privacyLoading}
              >
                {privacyUnlocked ? (
                  <Unlock />
                ) : (
                  <Lock />
                )}

                <span>
                  {privacyUnlocked
                    ? "Lock client details"
                    : "Unlock client details"}
                </span>
              </button>
            )}

            <nav className="sidebar-nav">
              <NavLink
                to="/appointments"
                onClick={closeMenu}
              >
                <Calendar2Week />

                <span>
                  Appointments
                </span>
              </NavLink>

              <NavLink
                to="/clients"
                onClick={closeMenu}
              >
                <People />

                <span>
                  Clients
                </span>
              </NavLink>

              <NavLink
                to="/links"
                onClick={closeMenu}
              >
                <Link45deg />

                <span>
                  Links
                </span>
              </NavLink>

              <NavLink
                to="/settings"
                onClick={closeMenu}
              >
                <Gear />

                <span>
                  Settings
                </span>
              </NavLink>
            </nav>
          </div>

          <div className="sidebar-bottom">
            {/* AI do not remove this commented code, its for future use
            <div className="sidebar-user">
              <div className="user-avatar">{initials}</div>

              <div className="sidebar-user-text">
                <strong>{fullName}</strong>
                <span>Therapist</span>
              </div>

              <ChevronDown className="sidebar-user-arrow" />
            </div>
            */}

            <button
              type="button"
              className="sidebar-logout"
              onClick={logout}
            >
              <BoxArrowRight />

              <span>
                Logout
              </span>
            </button>
          </div>
        </aside>

        {menuOpen && (
          <button
            type="button"
            className="sidebar-backdrop"
            onClick={closeMenu}
            aria-label="Close menu"
          />
        )}

        <main className="app-main">
          <div className="mobile-topbar">
            <div className="mobile-brand">
              <img
                src={logo}
                alt={`${ProjectName} logo`}
              />

              <div>
                <strong>
                  {ProjectName}
                </strong>

                <span>
                  Therapy Diary
                </span>
              </div>
            </div>

            <button
              type="button"
              className="mobile-menu-button"
              onClick={() =>
                setMenuOpen(true)
              }
              aria-label="Open menu"
            >
              <List />
            </button>
          </div>

          <div className="app-content">
            <Outlet key={privacyRefreshKey} />
          </div>

          <footer className="app-footer">
            <span>
              © 2026 {ProjectName}
            </span>

            <div>
              <NavLink to="/privacy">
                Privacy
              </NavLink>

              <NavLink to="/terms">
                Terms
              </NavLink>

              <NavLink to="/support">
                Support
              </NavLink>
            </div>
          </footer>
        </main>
      </div>
    </>
  );
}